// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 28-it-dashboard-ui.md
// Segmented control that replaces the shadcn underline tab bar on the
// app-detail page. Renders a pill track with a single sliding capsule that
// moves behind the active segment. The capsule position is measured from the
// DOM (offsetLeft/offsetWidth) so it naturally handles variable-width labels.
//
// Compatible with the existing Tabs value/onValueChange state — the parent
// keeps the Tabs wrapper; this component renders only the trigger row.
//
// Accessibility: the container has role="tablist"; each button has role="tab"
// and aria-selected. Keyboard: left/right arrows move focus + selection.
// prefers-reduced-motion: the slide transition is suppressed.
import * as React from 'react';

import { cn } from '@/lib/utils';

// Layout effect that no-ops to useEffect when there's no DOM, so a server render
// (or test environment without layout) doesn't warn. The app is a client SPA, so
// in practice this is always useLayoutEffect in the browser.
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect;

export interface SegmentedTabsItem {
  value: string;
  label: React.ReactNode;
  /** Optional data-testid forwarded to the button. */
  testId?: string;
  /** Ref callback forwarded to the button for external scroll-into-view. */
  refCallback?: (el: HTMLButtonElement | null) => void;
  /** id of the tabpanel this tab controls (sets aria-controls + the tab's id). */
  controls?: string;
  /** id assigned to the tab button (so the panel can aria-labelledby it). */
  id?: string;
}

export interface SegmentedTabsProps {
  items: SegmentedTabsItem[];
  value: string;
  onValueChange: (value: string) => void;
  /** aria-label for the tablist. */
  'aria-label'?: string;
  /** `sm` matches the 32px toolbar controls it sits beside. */
  size?: 'default' | 'sm';
  className?: string;
}

export function SegmentedTabs({
  items,
  value,
  onValueChange,
  'aria-label': ariaLabel = 'App sections',
  size = 'default',
  className,
}: SegmentedTabsProps) {
  // Outer element owns horizontal overflow; the inner track is content-width
  // (w-max) so it overflows the scroller rather than the page. The capsule is an
  // absolute child of the inner track, so it shares the track's coordinate space
  // and slides with its button when the scroller scrolls — no remeasure needed.
  const scrollerRef = React.useRef<HTMLDivElement>(null);
  const trackRef = React.useRef<HTMLDivElement>(null);
  const buttonRefs = React.useRef<Map<string, HTMLButtonElement>>(new Map());

  // Indicator geometry — left offset and width of the active button inside the
  // track. Null until measured (first paint + fonts ready).
  const [indicator, setIndicator] = React.useState<{ left: number; width: number } | null>(null);

  // Measure the active button's position relative to the (non-scrolling) inner
  // track. Both rects shift together when the scroller scrolls, so the delta is
  // stable content-space coordinate — no scrollLeft term required.
  const measure = React.useCallback(() => {
    const track = trackRef.current;
    const btn = buttonRefs.current.get(value);
    if (!track || !btn) return;
    const trackRect = track.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    setIndicator({
      left: btnRect.left - trackRect.left,
      width: btnRect.width,
    });
  }, [value]);

  // Measure on mount + value change in a layout effect so the capsule paints in
  // its resting position before the browser's first paint — otherwise the active
  // segment shows its bold label for one frame with no capsule behind it. Falls
  // back to useEffect when there's no DOM (SSR-safety; this is a client SPA).
  useIsomorphicLayoutEffect(() => {
    measure();
  }, [measure]);

  // Keep the active segment in view when the strip scrolls horizontally (narrow
  // viewports) — on mount, deep-link, or tab change a later tab can sit off the
  // visible edge with no cue that it's selected. `block: 'nearest'` avoids
  // nudging the page vertically; reduced-motion gets an instant jump. The
  // capsule's left is in the track's content coordinates, so it stays glued to
  // its button through this (and any manual) scroll without remeasuring.
  React.useEffect(() => {
    const btn = buttonRefs.current.get(value);
    if (!btn || typeof btn.scrollIntoView !== 'function') return;
    const reduce =
      typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
        : true;
    btn.scrollIntoView({
      inline: 'center',
      block: 'nearest',
      behavior: reduce ? 'auto' : 'smooth',
    });
  }, [value]);

  // Observe the inner track, not the scroller: the scroller's box is pinned to
  // the parent width and doesn't change when a label grows/shrinks (e.g. the
  // Access pending-grants badge toggling), whereas the w-max track's content box
  // tracks label widths — so this catches the active segment resizing and
  // remeasures the capsule. measure() reads only track-relative geometry, so a
  // pure viewport resize (scroller width changes, track unchanged) needs no
  // remeasure and correctly doesn't fire here.
  React.useEffect(() => {
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    const track = trackRef.current;
    if (ro && track) ro.observe(track);
    return () => {
      if (ro) ro.disconnect();
    };
  }, [measure]);

  React.useEffect(() => {
    if (typeof document !== 'undefined' && document.fonts) {
      // fonts.ready effectively never rejects; the catch only silences the
      // floating-promise lint without masking a meaningful error.
      void document.fonts.ready.then(measure).catch(() => {});
    }
  }, [measure]);

  // Whether the UA wants reduced motion — disables the CSS transition.
  const prefersReduced =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : true;

  // Keyboard arrow navigation: left/right within the tab list.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, idx: number) => {
    let next = -1;
    if (e.key === 'ArrowRight') next = (idx + 1) % items.length;
    if (e.key === 'ArrowLeft') next = (idx - 1 + items.length) % items.length;
    if (next === -1) return;
    e.preventDefault();
    const nextItem = items[next];
    if (!nextItem) return;
    onValueChange(nextItem.value);
    buttonRefs.current.get(nextItem.value)?.focus();
  };

  return (
    <div
      ref={scrollerRef}
      className={cn(
        // Scroller: constrained to its parent's width so the strip scrolls on
        // its own at narrow viewports instead of pushing the page sideways.
        'max-w-full overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        className,
      )}
    >
      <div
        ref={trackRef}
        role="tablist"
        aria-label={ariaLabel}
        className={cn(
          // Track: content-width muted pill, 4px padding, relative for the
          // absolute capsule. w-max so it overflows the scroller, not the page.
          'relative inline-flex w-max gap-0.5 rounded-[14px] bg-muted/50 border border-border/60 p-1',
          size === 'sm' && 'rounded-[10px] p-0.5',
        )}
      >
        {/* Sliding capsule — behind the buttons via negative z-index would clip,
          so we render it first (paint order) and keep buttons above via z-10. */}
        {indicator !== null && (
          <span
            aria-hidden="true"
            className={cn(
              'pointer-events-none absolute rounded-gl-icon bg-card ad-shadow ring-1 ring-inset ring-input',
              size === 'sm' ? 'top-0.5 bottom-0.5 rounded-[8px]' : 'top-1 bottom-1',
              !prefersReduced &&
                'transition-[left,width] duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)]',
            )}
            style={{ left: indicator.left, width: indicator.width }}
          />
        )}

        {items.map((item, idx) => {
          const active = item.value === value;
          return (
            <button
              key={item.value}
              role="tab"
              id={item.id}
              aria-selected={active}
              aria-controls={item.controls}
              tabIndex={active ? 0 : -1}
              data-testid={item.testId}
              ref={(el) => {
                if (el) buttonRefs.current.set(item.value, el);
                else buttonRefs.current.delete(item.value);
                item.refCallback?.(el);
              }}
              onClick={() => onValueChange(item.value)}
              onKeyDown={(e) => handleKeyDown(e, idx)}
              className={cn(
                // Segments: fixed height, horizontal padding, round pill shape.
                'relative z-10 rounded-gl-icon text-sm font-medium',
                size === 'sm'
                  ? 'inline-flex h-[26px] items-center justify-center rounded-[8px] px-2.5'
                  : 'h-9 px-4',
                'transition-colors duration-150',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                active ? 'text-foreground font-semibold' : 'text-foreground',
              )}
            >
              {item.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
